import * as React from 'react';
import clsx from 'clsx';
import { getStoryblokAlt } from '../../helpers/common';

const getStoryblokImageFocalPoint = (url, focus) => {
  if (!url || !focus) {
    return {
      x: '50%',
      y: '50%',
    };
  }
  const [imageWidth, imageHeight] = url.split('/')[5].split('x');
  const [focusWidth, focusHeight] = focus.split(':')[0].split('x');
  const percentageWidth = Math.round((focusWidth / imageWidth) * 100);
  const percentageHeight = Math.round((focusHeight / imageHeight) * 100);
  return {
    x: `${percentageWidth}%`,
    y: `${percentageHeight}%`,
  };
};

/**
 *
 * @param {Object} ratios - Object for ratios, e.g. { ratio: '3-2', ratioSm: '3-1' }
 * @param {String} ratios.ratio - auto | 4-3 | 16-9 | 3-2 | 3-1 | 1-1
 * @param {String} ratios.ratioXs - auto | 4-3 | 16-9 | 3-2 | 3-1 | 1-1
 * @param {String} ratios.ratioSm - auto | 4-3 | 16-9 | 3-2 | 3-1 | 1-1
 * @param {String} ratios.ratioMd - auto | 4-3 | 16-9 | 3-2 | 3-1 | 1-1
 * @param {String} ratios.ratioLg - auto | 4-3 | 16-9 | 3-2 | 3-1 | 1-1
 * @returns
 */
export default function StoryblokImage({
  src,
  ratio = 'auto',
  ratios = {},
  className,
  alt,
  figured = true,
  sizes,
  width,
  height,
  imageClassName,
  loading = 'lazy',
  fetchPriority,
}) {
  if (!src) return null;

  const Figure = figured ? 'figure' : 'div';
  const Picture = figured ? 'picture' : 'div';

  const {
    title, focus, copyright, name,
  } = src || {};
  let { filename } = src;

  if (!filename || filename === '') {
    filename = 'https://a.storyblok.com/f/120584/1000x1000/64d6bf549d/placeholder.png';
  }

  const isSvg = filename.includes('.svg');

  let altText = getStoryblokAlt(src, alt);
  if (!altText) {
    if (title) {
      altText = title;
    } else if (name) {
      altText = name;
    } else if (copyright) {
      altText = copyright;
    } else {
      altText = `${filename.split('/').at(-1).split('.')[0]}`;
    }
  }

  const _width = width || filename.split('/')[5].split('x')[0] || null;
  const _height = height || filename.split('/')[5].split('x')[1] || null;
  const intrinsicWidth = Number(_width) || 0;

  /**
   * see https://www.storyblok.com/docs/image-service#automatic-webp-support-detection
   */
  const fileNameToSize = (url, size, format = 'webp') => (
    isSvg ? `${url}` : `${url}/m/${size}x0/filters:format(${format})`
  );
  const requestedWidthCandidates = [
    120, 160, 200, 240, 300, 360, 420, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800, 1900, 2400, 3000, 3840,
  ].filter((candidate) => !intrinsicWidth || candidate <= intrinsicWidth);
  const widthCandidates = requestedWidthCandidates.length
    ? requestedWidthCandidates
    : [120, 160, 200, 240, 300, 360, 420, 500, 600, 700, 800];
  if (intrinsicWidth && !widthCandidates.includes(intrinsicWidth)) {
    widthCandidates.push(intrinsicWidth);
  }
  const fallbackSrcWidth = widthCandidates.includes(800)
    ? 800
    : widthCandidates[widthCandidates.length - 1];

  const storyblokImageFocalPoint = getStoryblokImageFocalPoint(filename, focus);

  const ratiosList = {
    auto: 'aspect-none',
    fit: 'aspect-none w-full h-full',
    '2:1': 'aspect-[2/1]',
    '1:1': 'aspect-[1/1]',
    '3:2': 'aspect-[3/2]',
    '3:1': 'aspect-[3/1]',
    '16:15': 'aspect-[16/15]',
    '16:14': 'aspect-[16/14]',
    '16:13': 'aspect-[16/13]',
    '16:12': 'aspect-[16/12]',
    '16:11': 'aspect-[16/11]',
    '16:10': 'aspect-[16/10]',
    '16:9': 'aspect-[16/9]',
    '16:8': 'aspect-[16/8]',
    '16:7': 'aspect-[16/7]',
    '16:6': 'aspect-[16/6]',
    '16:5': 'aspect-[16/5]',
    '16:4': 'aspect-[16/4]',
    '16:3': 'aspect-[16/3]',
    '16:2': 'aspect-[16/2]',
    '16:1': 'aspect-[16/1]',
    '15:16': 'aspect-[15/16]',
    '14:16': 'aspect-[14/16]',
    '13:16': 'aspect-[13/16]',
    '12:16': 'aspect-[12/16]',
    '11:16': 'aspect-[11/16]',
    '10:16': 'aspect-[10/16]',
    '9:16': 'aspect-[9/16]',
    '8:16': 'aspect-[8/16]',
    '7:16': 'aspect-[7/16]',
    '6:16': 'aspect-[6/16]',
    '5:16': 'aspect-[5/16]',
    '4:16': 'aspect-[4/16]',
    '3:16': 'aspect-[3/16]',
    '2:16': 'aspect-[2/16]',
    '1:16': 'aspect-[1/16]',
    '4:3': 'aspect-[4/3]',
    '4:2': 'aspect-[4/2]',
    '4:1': 'aspect-[4/1]',
  };

  const ratiosListLg = {
    auto: 'lg:aspect-none',
    fit: 'lg:w-full lg:h-full',
    '2:1': 'lg:aspect-[2/1]',
    '1:1': 'lg:aspect-[1/1]',
    '3:2': 'lg:aspect-[3/2]',
    '3:1': 'lg:aspect-[3/1]',
    '16:15': 'lg:aspect-[16/15]',
    '16:14': 'lg:aspect-[16/14]',
    '16:13': 'lg:aspect-[16/13]',
    '16:12': 'lg:aspect-[16/12]',
    '16:11': 'lg:aspect-[16/11]',
    '16:10': 'lg:aspect-[16/10]',
    '16:9': 'lg:aspect-[16/9]',
    '16:8': 'lg:aspect-[16/8]',
    '16:7': 'lg:aspect-[16/7]',
    '16:6': 'lg:aspect-[16/6]',
    '16:5': 'lg:aspect-[16/5]',
    '16:4': 'lg:aspect-[16/4]',
    '16:3': 'lg:aspect-[16/3]',
    '16:2': 'lg:aspect-[16/2]',
    '16:1': 'lg:aspect-[16/1]',
    '15:16': 'lg:aspect-[15/16]',
    '14:16': 'lg:aspect-[14/16]',
    '13:16': 'lg:aspect-[13/16]',
    '12:16': 'lg:aspect-[12/16]',
    '11:16': 'lg:aspect-[11/16]',
    '10:16': 'lg:aspect-[10/16]',
    '9:16': 'lg:aspect-[9/16]',
    '8:16': 'lg:aspect-[8/16]',
    '7:16': 'lg:aspect-[7/16]',
    '6:16': 'lg:aspect-[6/16]',
    '5:16': 'lg:aspect-[5/16]',
    '4:16': 'lg:aspect-[4/16]',
    '3:16': 'lg:aspect-[3/16]',
    '2:16': 'lg:aspect-[2/16]',
    '1:16': 'lg:aspect-[1/16]',
    '4:3': 'lg:aspect-[4/3]',
    '4:2': 'lg:aspect-[4/2]',
    '4:1': 'lg:aspect-[4/1]',
  };

  const ratiosListMd = {
    auto: 'md:aspect-none',
    fit: 'md:w-full md:h-full',
    '2:1': 'md:aspect-[2/1]',
    '1:1': 'md:aspect-[1/1]',
    '3:2': 'md:aspect-[3/2]',
    '3:1': 'md:aspect-[3/1]',
    '16:15': 'md:aspect-[16/15]',
    '16:14': 'md:aspect-[16/14]',
    '16:13': 'md:aspect-[16/13]',
    '16:12': 'md:aspect-[16/12]',
    '16:11': 'md:aspect-[16/11]',
    '16:10': 'md:aspect-[16/10]',
    '16:9': 'md:aspect-[16/9]',
    '16:8': 'md:aspect-[16/8]',
    '16:7': 'md:aspect-[16/7]',
    '16:6': 'md:aspect-[16/6]',
    '16:5': 'md:aspect-[16/5]',
    '16:4': 'md:aspect-[16/4]',
    '16:3': 'md:aspect-[16/3]',
    '16:2': 'md:aspect-[16/2]',
    '16:1': 'md:aspect-[16/1]',
    '15:16': 'md:aspect-[15/16]',
    '14:16': 'md:aspect-[14/16]',
    '13:16': 'md:aspect-[13/16]',
    '12:16': 'md:aspect-[12/16]',
    '11:16': 'md:aspect-[11/16]',
    '10:16': 'md:aspect-[10/16]',
    '9:16': 'md:aspect-[9/16]',
    '8:16': 'md:aspect-[8/16]',
    '7:16': 'md:aspect-[7/16]',
    '6:16': 'md:aspect-[6/16]',
    '5:16': 'md:aspect-[5/16]',
    '4:16': 'md:aspect-[4/16]',
    '3:16': 'md:aspect-[3/16]',
    '2:16': 'md:aspect-[2/16]',
    '1:16': 'md:aspect-[1/16]',
    '4:3': 'md:aspect-[4/3]',
    '4:2': 'md:aspect-[4/2]',
    '4:1': 'md:aspect-[4/1]',
  };

  const ratiosListSm = {
    auto: 'sm:aspect-none',
    fit: 'sm:w-full sm:h-full',
    '2:1': 'sm:aspect-[2/1]',
    '1:1': 'sm:aspect-[1/1]',
    '3:2': 'sm:aspect-[3/2]',
    '3:1': 'sm:aspect-[3/1]',
    '16:15': 'sm:aspect-[16/15]',
    '16:14': 'sm:aspect-[16/14]',
    '16:13': 'sm:aspect-[16/13]',
    '16:12': 'sm:aspect-[16/12]',
    '16:11': 'sm:aspect-[16/11]',
    '16:10': 'sm:aspect-[16/10]',
    '16:9': 'sm:aspect-[16/9]',
    '16:8': 'sm:aspect-[16/8]',
    '16:7': 'sm:aspect-[16/7]',
    '16:6': 'sm:aspect-[16/6]',
    '16:5': 'sm:aspect-[16/5]',
    '16:4': 'sm:aspect-[16/4]',
    '16:3': 'sm:aspect-[16/3]',
    '16:2': 'sm:aspect-[16/2]',
    '16:1': 'sm:aspect-[16/1]',
    '15:16': 'sm:aspect-[15/16]',
    '14:16': 'sm:aspect-[14/16]',
    '13:16': 'sm:aspect-[13/16]',
    '12:16': 'sm:aspect-[12/16]',
    '11:16': 'sm:aspect-[11/16]',
    '10:16': 'sm:aspect-[10/16]',
    '9:16': 'sm:aspect-[9/16]',
    '8:16': 'sm:aspect-[8/16]',
    '7:16': 'sm:aspect-[7/16]',
    '6:16': 'sm:aspect-[6/16]',
    '5:16': 'sm:aspect-[5/16]',
    '4:16': 'sm:aspect-[4/16]',
    '3:16': 'sm:aspect-[3/16]',
    '2:16': 'sm:aspect-[2/16]',
    '1:16': 'sm:aspect-[1/16]',
    '4:3': 'sm:aspect-[4/3]',
    '4:2': 'sm:aspect-[4/2]',
    '4:1': 'sm:aspect-[4/1]',
  };

  const ratiosListXs = {
    auto: 'xs:aspect-none',
    fit: 'xs:w-full xs:h-full',
    '2:1': 'xs:aspect-[2/1]',
    '1:1': 'xs:aspect-[1/1]',
    '3:2': 'xs:aspect-[3/2]',
    '3:1': 'xs:aspect-[3/1]',
    '16:15': 'xs:aspect-[16/15]',
    '16:14': 'xs:aspect-[16/14]',
    '16:13': 'xs:aspect-[16/13]',
    '16:12': 'xs:aspect-[16/12]',
    '16:11': 'xs:aspect-[16/11]',
    '16:10': 'xs:aspect-[16/10]',
    '16:9': 'xs:aspect-[16/9]',
    '16:8': 'xs:aspect-[16/8]',
    '16:7': 'xs:aspect-[16/7]',
    '16:6': 'xs:aspect-[16/6]',
    '16:5': 'xs:aspect-[16/5]',
    '16:4': 'xs:aspect-[16/4]',
    '16:3': 'xs:aspect-[16/3]',
    '16:2': 'xs:aspect-[16/2]',
    '16:1': 'xs:aspect-[16/1]',
    '15:16': 'xs:aspect-[15/16]',
    '14:16': 'xs:aspect-[14/16]',
    '13:16': 'xs:aspect-[13/16]',
    '12:16': 'xs:aspect-[12/16]',
    '11:16': 'xs:aspect-[11/16]',
    '10:16': 'xs:aspect-[10/16]',
    '9:16': 'xs:aspect-[9/16]',
    '8:16': 'xs:aspect-[8/16]',
    '7:16': 'xs:aspect-[7/16]',
    '6:16': 'xs:aspect-[6/16]',
    '5:16': 'xs:aspect-[5/16]',
    '4:16': 'xs:aspect-[4/16]',
    '3:16': 'xs:aspect-[3/16]',
    '2:16': 'xs:aspect-[2/16]',
    '1:16': 'xs:aspect-[1/16]',
    '4:3': 'xs:aspect-[4/3]',
    '4:2': 'xs:aspect-[4/2]',
    '4:1': 'xs:aspect-[4/1]',
  };

  let _ratio = ratio;
  if (ratios?.ratio) {
    _ratio = ratios.ratio;
  }

  const figureClass = clsx(
    (ratios?.ratio && ratiosList[ratios.ratio]) || (!ratios?.classNames && ratiosList[_ratio]),
    ratios?.ratioLg && ratiosListLg[ratios.ratioLg],
    ratios?.ratioMd && ratiosListMd[ratios.ratioMd],
    ratios?.ratioSm && ratiosListSm[ratios.ratioSm],
    ratios?.ratioXs && ratiosListXs[ratios.ratioXs],
    ratios?.classNames && ratios.classNames,
    _ratio !== 'auto' && 'relative',
    className,
  );

  const pictureClass = clsx(
    'w-full',
    (_ratio !== 'auto' || ratios?.classNames) && 'absolute top-0 right-0 bottom-0 left-0 w-full h-full overflow-hidden',
  );

  const imgClass = clsx(
    'm-auto',
    (_ratio !== 'auto' || ratios?.classNames) && 'object-cover w-full h-full object-[var(--focal-x)_var(--focal-y)]',
    imageClassName,
  );

  const focalStyles = {
    '--focal-x': storyblokImageFocalPoint.x,
    '--focal-y': storyblokImageFocalPoint.y,
  };
  return (
    <Figure className={figureClass} style={focalStyles}>
      <Picture className={pictureClass}>
        {!isSvg && (
          <source
            type="image/avif"
            srcSet={`
            ${widthCandidates.map((w) => `${fileNameToSize(filename, w, 'avif')} ${w}w`).join(', ')}`}
            sizes={sizes || '100vw'}
          />
        )}
        {!isSvg && (
          <source
            type="image/webp"
            srcSet={`
            ${widthCandidates.map((w) => `${fileNameToSize(filename, w, 'webp')} ${w}w`).join(', ')}`}
            sizes={sizes || '100vw'}
          />
        )}
        <img
          className={imgClass}
          src={isSvg ? filename : fileNameToSize(filename, fallbackSrcWidth, 'webp')}
          alt={altText}
          srcSet={!isSvg ? `
            ${widthCandidates.map((w) => `${fileNameToSize(filename, w, 'webp')} ${w}w`).join(', ')}` : ''}
          width={_width}
          height={_height}
          sizes={!isSvg && (sizes || '100vw')}
          loading={loading}
          decoding="async"
          fetchpriority={fetchPriority}
        />
      </Picture>
    </Figure>
  );
}
